/**
 * The route table.
 *
 * Every admin route sits behind `RequireAdmin`, which is a redirect for the
 * CONVENIENCE of an admin who is not signed in - not a security boundary. The
 * boundary is `deps.current_admin` on every `/admin` route server-side
 * (NFR-10.3.3): if this guard were removed tomorrow, every screen would render
 * and every request would 401 or 403.
 *
 * Spec 12's ten nav-minimum sections plus Categories and spec 18's
 * Announcements, in nav order.
 */

import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { ReactNode } from "react";

import { AppShell } from "./components/AppShell";
import { useAuth } from "./lib/auth";
import { LoginScreen } from "./pages/LoginScreen";
import { AnnouncementsScreen } from "./pages/AnnouncementsScreen";
import { DashboardScreen } from "./pages/DashboardScreen";
import { EmailCallbackScreen } from "./pages/EmailCallbackScreen";
import { EmailSettingsScreen } from "./pages/EmailSettingsScreen";
import { RegistrationsScreen } from "./pages/RegistrationsScreen";
import { MembersScreen } from "./pages/MembersScreen";
import { MemberDetailScreen } from "./pages/MemberDetailScreen";
import { MembershipsScreen } from "./pages/MembershipsScreen";
import { VendorsScreen } from "./pages/VendorsScreen";
import { HotlineScreen } from "./pages/HotlineScreen";
import { FamilyBizScreen } from "./pages/FamilyBizScreen";
import { FamilyBizDetailScreen } from "./pages/FamilyBizDetailScreen";
import { CategoriesScreen } from "./pages/CategoriesScreen";
import { ReferralsScreen } from "./pages/ReferralsScreen";
import { AiLogScreen } from "./pages/AiLogScreen";
import { AuditScreen } from "./pages/AuditScreen";
import { SettingsScreen } from "./pages/SettingsScreen";
import { NotFoundScreen } from "./pages/NotFoundScreen";

function RequireAdmin({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "restoring") {
    return (
      <div className="login">
        <div className="login__panel">
          <div className="skeleton" style={{ height: 22, width: "55%" }} />
          <div className="skeleton" style={{ height: 14, width: "80%" }} />
          <div className="skeleton" style={{ height: 14, width: "40%" }} />
        </div>
      </div>
    );
  }

  if (status === "signed-out") {
    // `state` carries the path so signing in returns the admin to the screen they
    // asked for rather than to the dashboard.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}

function LoginRoute() {
  const { status } = useAuth();
  if (status === "signed-in") return <Navigate to="/" replace />;
  return <LoginScreen />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route
        element={
          <RequireAdmin>
            <AppShell />
          </RequireAdmin>
        }
      >
        <Route index element={<DashboardScreen />} />
        <Route path="registrations" element={<RegistrationsScreen />} />
        <Route path="members" element={<MembersScreen />} />
        <Route path="members/:memberId" element={<MemberDetailScreen />} />
        <Route path="memberships" element={<MembershipsScreen />} />
        <Route path="announcements" element={<AnnouncementsScreen />} />
        <Route path="vendors" element={<VendorsScreen />} />
        <Route path="family-biz" element={<FamilyBizScreen />} />
        <Route path="family-biz/:listingId" element={<FamilyBizDetailScreen />} />
        <Route path="categories" element={<CategoriesScreen />} />
        <Route path="referrals" element={<ReferralsScreen />} />
        <Route path="ai-log" element={<AiLogScreen />} />
        <Route path="hotline" element={<HotlineScreen />} />
        <Route path="audit" element={<AuditScreen />} />
        <Route path="settings" element={<SettingsScreen />} />
        <Route path="settings/email" element={<EmailSettingsScreen />} />
        {/* Spec 20: must match FITZONE_ZOHO_REDIRECT_URI exactly. */}
        <Route path="settings/email/callback" element={<EmailCallbackScreen />} />
        <Route path="*" element={<NotFoundScreen />} />
      </Route>
    </Routes>
  );
}
